import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";
import api from "../utils/api";

const nextMondayAt = (hour = 16, minute = 0) => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const daysToMon = (8 - day) % 7 || 7;
  const target = new Date(now);
  target.setDate(now.getDate() + daysToMon);
  target.setHours(hour, minute, 0, 0);
  return target;
};

const formatNice = d =>
  d.toLocaleString("en-GB", {
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });

const clampDateAfter = (candidate, floorDate) => {
  if (!candidate || !floorDate) return candidate;
  return candidate.getTime() < floorDate.getTime() ? floorDate : candidate;
};

const LeaveForHeathrowPickup = () => {
  const [flightNumber, setFlightNumber] = useState("");
  const [terminal, setTerminal] = useState("T2");
  const [landingTime, setLandingTime] = useState(
    nextMondayAt().toISOString().slice(0, 16)
  );
  const [eGate, setEGate] = useState(true);
  const [bags, setBags] = useState("checked");
  const [profile, setProfile] = useState("standard");
  const [usePostcode, setUsePostcode] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [kids, setKids] = useState(false);
  const [mobility, setMobility] = useState(false);
  const [flightFetch, setFlightFetch] = useState({ status: "idle", message: "" });

  const queueEstimate = useMemo(() => {
    const scheduled = new Date(landingTime);
    if (Number.isNaN(scheduled.getTime())) return null;

    const hour = scheduled.getHours();
    const band =
      hour >= 4 && hour < 8
        ? { label: "Early morning (04:00–07:59)", immigration: [15, 30] }
        : hour >= 8 && hour < 12
        ? { label: "Morning peak (08:00–11:59)", immigration: [20, 45] }
        : hour >= 12 && hour < 16
        ? { label: "Midday (12:00–15:59)", immigration: [25, 55] }
        : hour >= 16 && hour < 21
        ? { label: "Evening peak (16:00–20:59)", immigration: [35, 75] }
        : { label: "Late (21:00–03:59)", immigration: [20, 45] };

    const adjust = ([min, max]) => {
      let adjMin = min;
      let adjMax = max;
      if (terminal === "T5") adjMax += 5;
      if (terminal === "T4") {
        adjMin += 5;
        adjMax += 10;
      }
      return [adjMin, adjMax];
    };

    const [min, max] = adjust(band.immigration);
    const startHour = (hour + 23) % 24;
    const endHour = (hour + 1) % 24;
    const fmtHr = h => `${String(h).padStart(2, "0")}:00`;

    return {
      band: band.label,
      min,
      max,
      snapshotWindow: `${fmtHr(startHour)}–${fmtHr(endHour)}`,
      confidence: hour >= 6 && hour <= 21 ? "Good data" : "Limited data"
    };
  }, [landingTime, terminal]);

  const readiness = useMemo(() => {
    const landing = new Date(landingTime);
    if (Number.isNaN(landing.getTime())) return null;

    // Taxi to stand
    let taxiMin = 7;
    let taxiMax = 12;
    if (terminal === "T4") {
      taxiMin += 2;
      taxiMax += 3;
    } else if (terminal === "T5") {
      taxiMax += 2;
    }

    // Immigration
    let immMin = eGate ? 8 : 25;
    let immMax = eGate ? 18 : 50;
    if (profile === "family") {
      immMin += 5;
      immMax += 10;
    } else if (profile === "assisted") {
      immMin += 15;
      immMax += 20;
    }
    if (kids) {
      immMin += 5;
      immMax += 10;
    }
    if (mobility) {
      immMin += 10;
      immMax += 15;
    }

    // Bags
    let bagMin = bags === "hand" ? 0 : 20;
    let bagMax = bags === "hand" ? 5 : 40;
    if (profile === "family" && bags !== "hand") {
      bagMin += 5;
      bagMax += 10;
    }
    if (profile === "assisted" && bags !== "hand") {
      bagMin += 10;
      bagMax += 10;
    }

    // Walk to exit
    let walkMin = 5;
    let walkMax = 10;
    if (terminal === "T5") walkMax += 5;
    if (terminal === "T4") {
      walkMin += 2;
      walkMax += 4;
    }

    const totalMin = taxiMin + immMin + bagMin + walkMin;
    const totalMax = taxiMax + immMax + bagMax + walkMax;

    const readyMin = new Date(landing.getTime() + totalMin * 60000);
    const readyMax = new Date(landing.getTime() + totalMax * 60000);

    // Hold zone: arrive near airport ~30 mins before earliest
    const holdTarget = clampDateAfter(
      new Date(readyMin.getTime() - 30 * 60000),
      new Date(landing.getTime() + 10 * 60000)
    );

    // Short stay entry window
    const shortStart = new Date(readyMin);
    const shortEnd = new Date(
      Math.min(readyMax.getTime(), readyMin.getTime() + 20 * 60000)
    );

    // Very light leave-home suggestion (only if postcode entered)
    let leaveBy = null;
    if (usePostcode && postcode.trim()) {
      let driveMin = 60; // conservative default
      if (profile === "family") driveMin += 5;
      if (profile === "assisted") driveMin += 10;
      leaveBy = new Date(shortStart.getTime() - driveMin * 60000);
    }

    return {
      landing,
      readyMin,
      readyMax,
      taxiMin,
      taxiMax,
      immMin,
      immMax,
      bagMin,
      bagMax,
      walkMin,
      walkMax,
      holdTarget,
      shortStart,
      shortEnd,
      leaveBy
    };
  }, [landingTime, eGate, bags, profile, kids, mobility, terminal, usePostcode, postcode]);

  const readinessCopy =
    readiness && readiness.readyMin && readiness.readyMax
      ? `Passenger likely ready ${formatNice(readiness.readyMin)} – ${formatNice(readiness.readyMax)}`
      : null;

  const handleFlightLookup = async () => {
    const clean = flightNumber.trim();
    if (!clean) {
      setFlightFetch({ status: "error", message: "Enter a flight number to fetch details." });
      return;
    }
    setFlightFetch({ status: "loading", message: "Checking flight…" });
    try {
      const res = await api.get(`/api/flights/by-number/${encodeURIComponent(clean)}`);
      const flight = res.data?.flight || res.data?.result || res.data;
      if (!flight) {
        setFlightFetch({ status: "error", message: "Flight not found. Please double-check the number." });
        return;
      }
      const arrDep = flight.arrival_or_departure;
      if (arrDep && arrDep !== "A") {
        setFlightFetch({ status: "error", message: "This looks like a departure flight. This tool is for arrivals/pickups." });
        return;
      }
      const term = flight.terminal || flight.terminal_number || flight.terminal_name || terminal;
      if (term) {
        const termStr = String(term).startsWith("T") ? String(term) : `T${term}`;
        setTerminal(termStr);
      }
      const sched = flight.scheduled_local || flight.scheduled_time_local || flight.scheduled_time;
      if (sched) {
        const iso = new Date(sched).toISOString().slice(0, 16);
        setLandingTime(iso);
      }
      setFlightFetch({
        status: "success",
        message: `Found arrival: Terminal ${term || "?"}, scheduled ${sched ? formatNice(new Date(sched)) : "unknown"}.`
      });
    } catch (err) {
      setFlightFetch({
        status: "error",
        message:
          err.response?.data?.error ||
          err.response?.data?.details ||
          "Could not fetch this flight. Please try again or confirm with your airline."
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Heathrow Arrival Pickup Time | When to Leave & Short Stay Window</title>
        <meta
          name="description"
          content="See when to pick up passengers at Heathrow: readiness window after landing, when to leave home, and the right Short Stay entry time. Includes immigration, bags, and terminal walk times."
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/heathrow-arrival-pickup-time"
        />
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-wrapper">
        <div className="parking-header">
          <h1>Heathrow Arrival Pickup Time</h1>
          <p>
            Answering the big question: when will your passenger be ready, and when should you enter Short Stay? Quick, bias-free guidance for Heathrow pickups.
          </p>
          <p style={{ fontSize: 12, color: "#475467" }}>Last Updated: 14 Jan 2026</p>
        </div>

        {/* Inputs */}
        <div className="parking-form">
          <div className="form-group">
            <label>Flight number (optional)</label>
            <input
              className="parking-input"
              placeholder="e.g. QR111"
              value={flightNumber}
              onChange={e => setFlightNumber(e.target.value)}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              If you know it, add it for clarity. We’ll still calculate with the details below.
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
              <button
                type="button"
                className="parking-btn"
                style={{ padding: "8px 14px", minWidth: 140 }}
                onClick={handleFlightLookup}
              >
                Use flight details
              </button>
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                We won’t track your flight — this just helps calculate realistic pickup timing.
              </span>
              {flightFetch.message && (
                <span
                  style={{
                    fontSize: 12,
                    color:
                      flightFetch.status === "success"
                        ? "#15803d"
                        : flightFetch.status === "loading"
                        ? "#0f172a"
                        : "#b91c1c"
                  }}
                >
                  {flightFetch.message}
                </span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Terminal</label>
            <select
              className="parking-input"
              value={terminal}
              onChange={e => setTerminal(e.target.value)}
            >
              <option value="T2">Terminal 2</option>
              <option value="T3">Terminal 3</option>
              <option value="T4">Terminal 4</option>
              <option value="T5">Terminal 5</option>
            </select>
          </div>

          <div className="form-group">
            <label>Landing time</label>
            <input
              type="datetime-local"
              className="parking-input"
              value={landingTime}
              onChange={e => setLandingTime(e.target.value)}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Prefilled to next Monday 16:00 for a realistic example.
            </div>
          </div>

          <div className="form-group">
            <label>eGate eligible?</label>
            <div className="option-grid option-grid-2">
              <label className="option-card">
                <input
                  type="radio"
                  name="egate"
                  value="yes"
                  checked={eGate}
                  onChange={() => setEGate(true)}
                />
                <div className="option-text">
                  <span className="option-title">Yes</span>
                  <span className="option-sub">Eligible passports / visas.</span>
                </div>
              </label>
              <label className="option-card">
                <input
                  type="radio"
                  name="egate"
                  value="no"
                  checked={!eGate}
                  onChange={() => setEGate(false)}
                />
                <div className="option-text">
                  <span className="option-title">No</span>
                  <span className="option-sub">Manual border control.</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Bags</label>
            <div className="option-grid option-grid-2">
              <label className="option-card">
                <input
                  type="radio"
                  name="bags"
                  value="hand"
                  checked={bags === "hand"}
                  onChange={() => setBags("hand")}
                />
                <div className="option-text">
                  <span className="option-title">Hand baggage only</span>
                  <span className="option-sub">Fastest exit.</span>
                </div>
              </label>
              <label className="option-card">
                <input
                  type="radio"
                  name="bags"
                  value="checked"
                  checked={bags === "checked"}
                  onChange={() => setBags("checked")}
                />
                <div className="option-text">
                  <span className="option-title">Checked bags</span>
                  <span className="option-sub">Allow for reclaim.</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Traveller profile</label>
            <div className="option-grid option-grid-3">
              <label className="option-card">
                <input
                  type="radio"
                  name="profile"
                  value="standard"
                  checked={profile === "standard"}
                  onChange={() => setProfile("standard")}
                />
                <div className="option-text">
                  <span className="option-title">Standard</span>
                  <span className="option-sub">Typical traveller.</span>
                </div>
              </label>
              <label className="option-card">
                <input
                  type="radio"
                  name="profile"
                  value="family"
                  checked={profile === "family"}
                  onChange={() => setProfile("family")}
                />
                <div className="option-text">
                  <span className="option-title">Family</span>
                  <span className="option-sub">Extra buffer for kids + bags.</span>
                </div>
              </label>
              <label className="option-card">
                <input
                  type="radio"
                  name="profile"
                  value="assisted"
                  checked={profile === "assisted"}
                  onChange={() => setProfile("assisted")}
                />
                <div className="option-text">
                  <span className="option-title">Assisted</span>
                  <span className="option-sub">Mobility / buggy / escort.</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="option-card" style={{ alignItems: "center" }}>
              <input
                type="checkbox"
                checked={usePostcode}
                onChange={e => setUsePostcode(e.target.checked)}
              />
              <span className="option-title" style={{ marginLeft: 8 }}>
                I’m driving from a postcode (optional)
              </span>
            </label>
            {usePostcode && (
              <input
                className="parking-input"
                placeholder="e.g. SW6 5HJ"
                value={postcode}
                onChange={e => setPostcode(e.target.value)}
                style={{ marginTop: 8 }}
              />
            )}
          </div>

          <details className="leave-section-card leave-collapsible" style={{ marginTop: 8 }} open={showAdvanced}>
            <summary className="leave-summary-bar" onClick={e => { e.preventDefault(); setShowAdvanced(!showAdvanced); }}>
              ⚙️ Advanced (kids, mobility)
            </summary>
            {showAdvanced && (
              <div className="leave-collapsible-body" style={{ paddingTop: 8 }}>
                <label className="option-card" style={{ marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={kids}
                    onChange={e => setKids(e.target.checked)}
                  />
                  <span className="option-title" style={{ marginLeft: 8 }}>
                    Travelling with kids under 10
                  </span>
                </label>
                <label className="option-card">
                  <input
                    type="checkbox"
                    checked={mobility}
                    onChange={e => setMobility(e.target.checked)}
                  />
                  <span className="option-title" style={{ marginLeft: 8 }}>
                    Mobility assistance / buggy expected
                  </span>
                </label>
              </div>
            )}
          </details>
        </div>

        {/* Results */}
        {readiness && (
          <div className="parking-results">
            <div className="leave-hero-card">
              <div className="leave-hero-title">Passenger ready window (when to meet)</div>
              <div className="leave-hero-time">
                {formatNice(readiness.readyMin)} – {formatNice(readiness.readyMax)}
              </div>
              <div className="leave-hero-sub">
                Passenger likely ready to meet at Arrivals for {terminal} landing at {formatNice(readiness.landing)}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#f8fafc",
                  marginTop: 6,
                  lineHeight: 1.5,
                  textShadow: "0 1px 2px rgba(0,0,0,0.35)"
                }}
              >
                Typical {bags === "hand" ? "hand-baggage" : "checked-bag"} exit with {eGate ? "eGate" : "manual border"}:
                {" "}
                {queueEstimate ? `${queueEstimate.min}–${queueEstimate.max} mins immigration (band: ${queueEstimate.band.toLowerCase()}). ` : ""}
                Includes taxi-in, immigration, baggage, terminal walk. Can be longer at peaks.
              </div>
              <div style={{ fontSize: 12, color: "#e0f2fe", marginTop: 6, textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>
                Example timing shown. Enter your flight number for live, flight-specific details.
              </div>
              <div style={{ fontSize: 12, color: "#e0f2fe", marginTop: 6, textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>
                This window reflects typical exit times for this terminal and time of day. Always wait for passenger confirmation before entering Short Stay.
              </div>
              <div style={{ marginTop: 10, fontSize: 13, display: "flex", gap: 10, alignItems: "center" }}>
                <span
                  style={{
                    background: "#0f172a",
                    color: "#e0f2fe",
                    padding: "4px 8px",
                    borderRadius: 8,
                    fontWeight: 600
                  }}
                >
                  {queueEstimate?.confidence || "Limited data"}
                </span>
                {usePostcode && readiness.leaveBy && postcode.trim() && (
                  <span style={{ color: "#f8fafc", textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>
                    Leave home by {formatNice(readiness.leaveBy)} (incl. buffer).
                  </span>
                )}
              </div>
            </div>

            <div
              className="timing-breakdown"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 12
              }}
            >
              <div
                className="breakdown-card"
                style={{ border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: 12 }}
              >
                <div className="breakdown-title">Passenger ready time</div>
                <div className="breakdown-value">
                  {readinessCopy || "—"}
                </div>
                <ul style={{ marginTop: 8, lineHeight: 1.5, paddingLeft: 16 }}>
                  <li>Taxi to stand: ~{readiness.taxiMin}–{readiness.taxiMax} mins</li>
                  <li>Border ({eGate ? "eGate" : "desk"}): ~{readiness.immMin}–{readiness.immMax} mins</li>
                  <li>Bags: ~{readiness.bagMin}–{readiness.bagMax} mins</li>
                  <li>Walk to exit: ~{readiness.walkMin}–{readiness.walkMax} mins</li>
                </ul>
              </div>

              <div
                className="breakdown-card"
                style={{ border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: 12 }}
              >
                <div className="breakdown-title">Hold zone recommendation</div>
                <div className="breakdown-value">
                  Arrive near Heathrow around {formatNice(readiness.holdTarget)}
                </div>
                <p style={{ marginTop: 8, lineHeight: 1.5 }}>
                  Wait at a nearby holding area until the passenger confirms they are landside.
                  Avoid entering Short Stay too early to prevent extra charges.
                </p>
              </div>

              <div
                className="breakdown-card"
                style={{ border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: 12 }}
              >
                <div className="breakdown-title">Short Stay entry window</div>
                <div className="breakdown-value">
                  {formatNice(readiness.shortStart)} – {formatNice(readiness.shortEnd)}
                </div>
                <p style={{ marginTop: 8, lineHeight: 1.5 }}>
                  Enter between these times to minimise parking costs. Earlier entry risks extra fees.
                </p>
              </div>
            </div>

            {queueEstimate && (
              <div className="leave-section-card">
                <h3 className="leave-section-title">Queue snapshot for this time</h3>
                <p style={{ marginBottom: 6, lineHeight: 1.5 }}>
                  Immigration at this time: usually {queueEstimate.min}–{queueEstimate.max} mins ({queueEstimate.band.toLowerCase()} pattern).
                </p>
                <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                  Window checked: {queueEstimate.snapshotWindow}. Based on recent Heathrow snapshot patterns by terminal.
                </p>
              </div>
            )}

            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">🧠 How we worked this out</summary>
              <div className="leave-explainer leave-collapsible-body" style={{ lineHeight: 1.6 }}>
                <p>We combine terminal time-bands, eGate eligibility, baggage choice, and family/assisted flags to build a realistic readiness window.</p>
                <p>Immigration ranges use recent Heathrow snapshot patterns for the selected time band; checked bags add reclaim time; T4/T5 walks add a little extra.</p>
                <p>Weather, staffing, multiple widebody arrivals or diversions can extend these times. Always follow airport instructions on the day.</p>
              </div>
            </details>

            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">🚗 Pickup tips (avoid stress & charges)</summary>
              <ul className="leave-explainer leave-collapsible-body" style={{ marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Don’t enter Short Stay until the passenger confirms they are at the exit.</li>
                <li>Ask them to message when at baggage belt → border → exit.</li>
                <li>Checked bags and T5 remote stands can add 15–25 minutes.</li>
                <li>Keep an eye on live flight status; early or late arrivals shift this window.</li>
                <li>If using eGates, delays can still occur during evening peaks.</li>
              </ul>
            </details>

            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">❓ FAQs</summary>
              <ul className="leave-explainer leave-collapsible-body" style={{ marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li><strong>How long after landing should I arrive for pickup?</strong> Typically 45–90 minutes for long-haul, 30–60 minutes for European, 15–30 minutes for domestic. Peaks can extend up to 2 hours.</li>
                <li><strong>Do eGates make it faster?</strong> Yes, they reduce immigration time, but evening peaks can still slow queues.</li>
                <li><strong>Which terminal takes longest to exit?</strong> It varies; T5 can be longer due to size/stands, T4 can slow down due to layout.</li>
                <li><strong>What if the flight is early or late?</strong> Adjust the window using the new landing time; hold outside Short Stay until the passenger is landside.</li>
                <li><strong>Should I enter Short Stay before the passenger is ready?</strong> No. Enter only when they confirm they are at the exit to avoid extra charges.</li>
                <li><strong>Can I wait for free?</strong> Use nearby holding areas or long stay waiting; Short Stay charges start on entry.</li>
              </ul>
            </details>

            <div style={{ marginTop: 18, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
              <strong>Need a door-to-terminal plan?</strong>
              <p style={{ marginTop: 6, marginBottom: 6, lineHeight: 1.5 }}>
                Get a personalised leave-home time with live-style buffers, taxi vs parking options, and terminal drop-off routes.
              </p>
              <a href="/leave-for-heathrow" className="parking-btn" style={{ display: "inline-block", textDecoration: "none" }}>
                Go to the full calculator
              </a>
            </div>

            <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
              <strong>Plan your Heathrow journey:</strong>
              <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
                <li>✈️ <a href="/flight-checker">Check your flight</a></li>
                <li>🚕 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
                <li>🚗 <a href="/heathrow-dropoff-calculator">Drop-off fees & exemptions</a></li>
                <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
                <li>🕒 <a href="/how-early-to-arrive-heathrow">How early to arrive (departures)</a></li>
              </ul>
            </div>
          </div>
        )}

        <div style={{ marginTop: 40 }}>
          <ReviewsBar />
        </div>
      </div>
    </>
  );
};

export default LeaveForHeathrowPickup;
